import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "../lib/api";
import { CONTACT_INFO } from "../data/brands";

const initial = { name: "", email: "", phone: "", company: "", message: "" };

export default function ContactForm({ brand = "group", heading, blurb }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitContact({ ...form, brand });
      setDone(true);
      setForm(initial);
      toast.success("Thank you, your enquiry has been received.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border-0 border-b border-dejure-silver/40 bg-transparent py-4 font-sans text-base text-dejure-ink placeholder:text-dejure-ink/30 focus:border-dejure-purple focus:outline-none focus:ring-0 transition-colors duration-300";

  return (
    <section id="contact" className="bg-dejure-ivory" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-36">
        <div className="grid gap-16 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="font-accent text-xs uppercase tracking-[0.25em] text-dejure-purple">
              Get in touch
            </p>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter text-dejure-ink">
              {heading || "Begin a conversation"}
            </h2>
            <p className="mt-8 max-w-md font-sans text-base md:text-lg leading-relaxed text-dejure-ink/60">
              {blurb ||
                "Tell us what you are working towards. Our advisors will respond with considered, confidential guidance."}
            </p>

            <div className="mt-12 space-y-5">
              <div className="border-t border-dejure-silver/30 pt-5">
                <p className="font-accent text-[0.65rem] uppercase tracking-[0.25em] text-dejure-ink/40">Email</p>
                <a href={`mailto:${CONTACT_INFO.email}`} className="mt-1 block font-sans text-lg text-dejure-ink hover:text-dejure-purple transition-colors duration-300">
                  {CONTACT_INFO.email}
                </a>
              </div>
              <div className="border-t border-dejure-silver/30 pt-5">
                <p className="font-accent text-[0.65rem] uppercase tracking-[0.25em] text-dejure-ink/40">Address</p>
                <p className="mt-1 font-sans text-lg text-dejure-ink">United Arab Emirates</p>
                <p className="mt-1 font-sans text-sm text-dejure-ink/50">(Setting up a new location to host you)</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 md:p-12 shadow-[0_8px_40px_rgba(0,0,0,0.05)] border border-dejure-silver/20">
            {done ? (
              <motion.div
                data-testid="contact-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex h-full min-h-[380px] flex-col items-start justify-center"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-dejure-purple text-white">
                  <Check size={26} />
                </span>
                <h3 className="mt-8 font-serif text-4xl tracking-tighter text-dejure-ink">Thank you.</h3>
                <p className="mt-4 max-w-sm font-sans text-base text-dejure-ink/60">
                  Your enquiry has been received. A member of the De Jure team will be in touch shortly.
                </p>
                <button
                  data-testid="contact-reset"
                  onClick={() => setDone(false)}
                  className="mt-8 font-accent text-xs uppercase tracking-[0.2em] text-dejure-purple hover:text-dejure-purple-dark transition-colors duration-300"
                >
                  Send another message
                </button>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-7">
                <div className="grid gap-7 sm:grid-cols-2">
                  <div>
                    <label className="font-accent text-[0.65rem] uppercase tracking-[0.2em] text-dejure-ink/50">Full name *</label>
                    <input data-testid="contact-name" value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" />
                  </div>
                  <div>
                    <label className="font-accent text-[0.65rem] uppercase tracking-[0.2em] text-dejure-ink/50">Email *</label>
                    <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@company.com" />
                  </div>
                  <div>
                    <label className="font-accent text-[0.65rem] uppercase tracking-[0.2em] text-dejure-ink/50">Phone</label>
                    <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="+971 ..." />
                  </div>
                  <div>
                    <label className="font-accent text-[0.65rem] uppercase tracking-[0.2em] text-dejure-ink/50">Company</label>
                    <input data-testid="contact-company" value={form.company} onChange={update("company")} className={inputCls} placeholder="Organisation" />
                  </div>
                </div>
                <div>
                  <label className="font-accent text-[0.65rem] uppercase tracking-[0.2em] text-dejure-ink/50">How can we help? *</label>
                  <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={4} className={inputCls + " resize-none"} placeholder="Tell us about your requirements" />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-3 bg-dejure-purple px-9 py-4 font-accent text-xs uppercase tracking-[0.2em] text-white hover:bg-dejure-purple-dark disabled:opacity-60 transition-colors duration-300"
                >
                  {loading ? "Sending…" : "Send enquiry"}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
